import { memo, useCallback, useMemo } from 'react'
import { GroupedVirtuoso } from 'react-virtuoso'
import { WorkerEvaluationDateGroup } from 'api/review/getWorkReview'
import { Box, Flex, Loading } from 'ui'
import { useReviewEvaluateV2ContextValue } from '../context/ReviewEvaluateV2Provider'
import { GroupHeader } from './GroupHeader'
import { WorkerEvaluationShiftCard } from './WorkerEvaluationShiftCard'

function getShiftFromGroups(
  groups: WorkerEvaluationDateGroup[],
  groupIndex: number,
  shiftIndex: number
) {
  /**
   * Virtuoso provides groupIndex (index in the groups array) and shiftIndex
   * (index across all grouped shifts). Convert shiftIndex to the local index
   * within its group.
   */
  const totalShiftsInPreviousGroups = groups
    .slice(0, groupIndex)
    .reduce((acc, group) => acc + group.shifts.length, 0)

  const localIndex = shiftIndex - totalShiftsInPreviousGroups
  const group = groups[groupIndex]

  if (!group) {
    return null
  }

  return group.shifts[localIndex] ?? null
}

function getShiftByFlatIndex(
  groups: WorkerEvaluationDateGroup[],
  shiftIndex: number
) {
  let remaining = shiftIndex

  for (const group of groups) {
    if (remaining < group.shifts.length) {
      return group.shifts[remaining] ?? null
    }
    remaining -= group.shifts.length
  }

  return null
}

const ShiftCardContent = memo(function ShiftCardContent({
  groupIndex,
  shiftIndex,
}: {
  groupIndex: number
  shiftIndex: number
}) {
  const {
    state: { groups },
  } = useReviewEvaluateV2ContextValue()

  const shift = useMemo(
    () => getShiftFromGroups(groups, groupIndex, shiftIndex),
    [groups, groupIndex, shiftIndex]
  )

  if (!shift) {
    return null
  }

  return <WorkerEvaluationShiftCard key={shift.id} shift={shift} />
})

const GroupedShiftListWithoutMemo = () => {
  const {
    actions: { fetchNextPage },
    state: {
      filters,
      groups,
      queryState: { isFetchingNextPage, hasNextPage },
    },
  } = useReviewEvaluateV2ContextValue()

  const { sortedGroupKeys, groupCounts } = useMemo(() => {
    return {
      sortedGroupKeys: groups.map((group) => group.date),
      groupCounts: groups.map((group) => group.shifts.length),
    }
  }, [groups])

  const listKey = useMemo(
    () =>
      [
        filters.reviewStatus,
        filters.startDate?.toISOString() ?? '',
        filters.endDate?.toISOString() ?? '',
        filters.locationId ?? '',
        filters.positionId ?? '',
        filters.workerId ?? '',
      ].join('|'),
    [filters]
  )

  const itemContent = useCallback((shiftIndex: number, groupIndex: number) => {
    return <ShiftCardContent groupIndex={groupIndex} shiftIndex={shiftIndex} />
  }, [])

  const computeItemKey = useCallback(
    (shiftIndex: number) => {
      const shift = getShiftByFlatIndex(groups, shiftIndex)
      return shift?.id ?? `idx-${shiftIndex}`
    },
    [groups]
  )

  return (
    <Box>
      <GroupedVirtuoso
        key={listKey}
        useWindowScroll
        groupCounts={groupCounts}
        groupContent={(index) => (
          <GroupHeader title={sortedGroupKeys[index] || 'N/A'} />
        )}
        itemContent={itemContent}
        computeItemKey={computeItemKey}
        atBottomThreshold={500}
        endReached={() => {
          if (hasNextPage) {
            fetchNextPage()
          }
        }}
      />
      {isFetchingNextPage ? (
        <Flex justifyContent="center" py={3}>
          <Loading />
        </Flex>
      ) : null}
    </Box>
  )
}

export const GroupedShiftList = memo(GroupedShiftListWithoutMemo)
